import React, { useEffect } from 'react';

import { useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import Input from 'components/Inputs/Input';
import Select from 'components/Select';
import PhoneNumberInput from 'components/Inputs/PhoneNumberInput';
import { getMessageForInvalidFields, getMessageForRequiredFields, validatePhoneNumber } from 'utils/validationUtils';
import {
  calculateTotalPercentageOwned,
  convertPercentageValue,
  convertTotalOwnersValueToNumber,
  getVariableDisplayName,
  getVariableKey,
} from 'pages/ContractorPortal/steps/ContractorApplication/сontractorApplication.utils';
import {
  InputLabel,
  IntermediaryOwnerVariable,
} from 'pages/ContractorPortal/steps/ContractorApplication/contractorApplication.enums';
import {
  COMPANY_OWNERS_AMOUNT_OPTIONS,
  DEFAULT_REQUIRED_MESSAGE,
  MAX_INTERMEDIARY_OWNERS,
  MAX_PERCENTAGE_OWNED,
  MIN_PERCENTAGE_OWNED,
  PERCENT_PLACEHOLDER,
} from 'pages/ContractorPortal/steps/ContractorApplication/сontractorApplication.constants';
import styles from 'pages/ContractorPortal/steps/ContractorApplication/ContractorApplication.module.scss';

const OwnerInformation = () => {
  const {
    register,
    unregister,
    setValue,
    control,
    watch,
    getValues,
    trigger,
    formState: { errors },
    clearErrors,
  } = useFormContext();
  const watcher = watch();

  useEffect(() => {
    register('intermediary_total_owners', {
      required: {
        value: !watcher.intermediary_total_owners,
        message: DEFAULT_REQUIRED_MESSAGE,
      },
    });
    register('totalPercentageOwned', {
      validate: () =>
        calculateTotalPercentageOwned(watcher) <= MAX_PERCENTAGE_OWNED ||
        'Total Percentage For All Owners Must Not Exceed 100%',
    });
  }, [register, unregister, watcher]);

  useEffect(() => {
    const ownersCount = convertTotalOwnersValueToNumber(watcher.intermediary_total_owners);

    for (let index = 1; index <= ownersCount; index++) {
      register(getVariableKey(IntermediaryOwnerVariable.FirstName, index), {
        required: {
          value: !watcher[getVariableKey(IntermediaryOwnerVariable.FirstName, index)],
          message: getMessageForRequiredFields(getVariableDisplayName(InputLabel.OwnerFirstName, index)),
        },
      });
      register(getVariableKey(IntermediaryOwnerVariable.LastName, index), {
        required: {
          value: !watcher[getVariableKey(IntermediaryOwnerVariable.LastName, index)],
          message: getMessageForRequiredFields(getVariableDisplayName(InputLabel.OwnerLastName, index)),
        },
      });
      register(getVariableKey(IntermediaryOwnerVariable.PercentageOwned, index), {
        required: {
          value: !watcher[getVariableKey(IntermediaryOwnerVariable.PercentageOwned, index)],
          message: getMessageForRequiredFields(getVariableDisplayName(InputLabel.OwnerPercentageOwnedOfCompany, index)),
        },
        validate: (value) => {
          const percentValue = parseFloat(value + '%');

          if (isNaN(percentValue)) {
            return getMessageForInvalidFields(getVariableDisplayName(InputLabel.OwnerPercentageOwnedOfCompany, index));
          } else {
            switch (true) {
              case percentValue < MIN_PERCENTAGE_OWNED: {
                return 'Must Not Be Less Than 25%';
              }
              case percentValue > MAX_PERCENTAGE_OWNED: {
                return 'Must Not Be Greater Than 100%';
              }
            }
          }
        },
      });
      register(getVariableKey(IntermediaryOwnerVariable.CellPhone, index), {
        required: {
          value: !watcher[getVariableKey(IntermediaryOwnerVariable.CellPhone, index)],
          message: getMessageForRequiredFields(getVariableDisplayName(InputLabel.OwnerPersonalCellPhone, index)),
        },
        validate: (value) => {
          for (let i = 1; i <= ownersCount; i++) {
            if (index !== i && value && value === getValues(getVariableKey(IntermediaryOwnerVariable.CellPhone, i))) {
              return `${InputLabel.OwnerPersonalCellPhone} should be a unique phone number.`;
            }
          }

          return validatePhoneNumber(value);
        },
      });
    }

    return () => {
      for (let index = 1; index <= MAX_INTERMEDIARY_OWNERS; index++) {
        unregister([
          getVariableKey(IntermediaryOwnerVariable.FirstName, index),
          getVariableKey(IntermediaryOwnerVariable.LastName, index),
          getVariableKey(IntermediaryOwnerVariable.PercentageOwned, index),
          getVariableKey(IntermediaryOwnerVariable.CellPhone, index),
        ]);
      }
    };
  }, [register, unregister, watcher.intermediary_total_owners]);

  const renderOwnersInformation = () => {
    const ownersInformation = [];

    for (let index = 1; index <= convertTotalOwnersValueToNumber(watcher.intermediary_total_owners); index++) {
      ownersInformation.push(
        <div key={getVariableKey(IntermediaryOwnerVariable.FirstName, index)}>
          <div className={styles.form_row}>
            <Input
              label={getVariableDisplayName(InputLabel.OwnerFirstName, index)}
              name={getVariableKey(IntermediaryOwnerVariable.FirstName, index)}
              value={watcher[getVariableKey(IntermediaryOwnerVariable.FirstName, index)]}
              errorMessage={errors[getVariableKey(IntermediaryOwnerVariable.FirstName, index)]?.message}
              inputContainerClassName={styles.input}
              onChange={(event) => {
                clearErrors(getVariableKey(IntermediaryOwnerVariable.FirstName, index));
                setValue(getVariableKey(IntermediaryOwnerVariable.FirstName, index), event.target.value);
              }}
            />
            <Input
              label={getVariableDisplayName(InputLabel.OwnerLastName, index)}
              name={getVariableKey(IntermediaryOwnerVariable.LastName, index)}
              value={watcher[getVariableKey(IntermediaryOwnerVariable.LastName, index)]}
              errorMessage={errors[getVariableKey(IntermediaryOwnerVariable.LastName, index)]?.message}
              inputContainerClassName={styles.input}
              onChange={(event) => {
                clearErrors(getVariableKey(IntermediaryOwnerVariable.LastName, index));
                setValue(getVariableKey(IntermediaryOwnerVariable.LastName, index), event.target.value);
              }}
            />
          </div>
          <div className={styles.form_row}>
            <Input
              label={getVariableDisplayName(InputLabel.OwnerPercentageOwnedOfCompany, index)}
              name={getVariableKey(IntermediaryOwnerVariable.PercentageOwned, index)}
              inputContainerClassName={styles.input}
              placeholder={PERCENT_PLACEHOLDER}
              errorMessage={errors[getVariableKey(IntermediaryOwnerVariable.PercentageOwned, index)]?.message}
              onChange={(event) => {
                clearErrors([getVariableKey(IntermediaryOwnerVariable.PercentageOwned, index), 'totalPercentageOwned']);
                setValue(getVariableKey(IntermediaryOwnerVariable.PercentageOwned, index), event.target.value);

                if (errors[getVariableKey(IntermediaryOwnerVariable.PercentageOwned, index)]?.message) {
                  trigger(getVariableKey(IntermediaryOwnerVariable.PercentageOwned, index));
                }
              }}
              value={convertPercentageValue(watcher[getVariableKey(IntermediaryOwnerVariable.PercentageOwned, index)])}
            />
            <PhoneNumberInput
              label={getVariableDisplayName(InputLabel.OwnerPersonalCellPhone, index)}
              name={getVariableKey(IntermediaryOwnerVariable.CellPhone, index)}
              value={watcher[getVariableKey(IntermediaryOwnerVariable.CellPhone, index)]}
              errorMessage={errors[getVariableKey(IntermediaryOwnerVariable.CellPhone, index)]?.message}
              className={styles.input}
              inputContainerClassname={styles.input}
              onChange={(value: string) => {
                clearErrors(getVariableKey(IntermediaryOwnerVariable.CellPhone, index));
                setValue(getVariableKey(IntermediaryOwnerVariable.CellPhone, index), value);
              }}
              onBlur={() => {
                if (watcher[getVariableKey(IntermediaryOwnerVariable.CellPhone, index)]) {
                  trigger(getVariableKey(IntermediaryOwnerVariable.CellPhone, index));
                }
              }}
            />
          </div>
        </div>,
      );
    }

    return ownersInformation;
  };

  return (
    <>
      <div className={styles.blockHeader}>Owner Information</div>
      <div className={styles.form_row}>
        <Select
          label={InputLabel.HowManyOwners}
          name="intermediary_total_owners"
          useFormControl
          control={control}
          required
          options={COMPANY_OWNERS_AMOUNT_OPTIONS}
          value={COMPANY_OWNERS_AMOUNT_OPTIONS.find((option) => option.label === watcher.intermediary_total_owners)}
          errorMessage={errors?.intermediary_total_owners?.message}
          inputContainerClassName={styles.input}
          className={styles.dropdown}
          isClearable={!!watcher.intermediary_total_owners}
        />
      </div>
      {renderOwnersInformation()}
      <ErrorMessage
        name={'totalPercentageOwned'}
        errors={errors}
        render={({ message }) => <div className={styles.errorMessage}>{message}</div>}
      />
    </>
  );
};

export default OwnerInformation;
